import { supabase } from "../supabase/client";

export type HousingForecastSettings = {
  version: 1;
  taxProfileId?: string | null;
  assets: any[];
  selectedAssetId: string;
  saleInputs: any;
  projectionInputs: any;
};

export type HousingForecastProfile = {
  id: string;
  name: string;
  settings: HousingForecastSettings;
  createdAt: number;
};

type DbHousingForecastProfile = {
  id: string;
  name: string;
  settings: any;
  created_at: string;
};

export const HOUSING_FORECAST_PROFILES_CHANGED_EVENT =
  "housingForecastProfiles:changed";

export const notifyHousingForecastProfilesChanged = () => {
  window.dispatchEvent(new Event(HOUSING_FORECAST_PROFILES_CHANGED_EVENT));
};

export const onHousingForecastProfilesChanged = (handler: () => void) => {
  window.addEventListener(HOUSING_FORECAST_PROFILES_CHANGED_EVENT, handler);
  return () =>
    window.removeEventListener(
      HOUSING_FORECAST_PROFILES_CHANGED_EVENT,
      handler
    );
};

const mapDbToProfile = (
  row: DbHousingForecastProfile
): HousingForecastProfile => {
  return {
    id: row.id,
    name: row.name,
    settings: row.settings,
    createdAt: Number.isFinite(Date.parse(row.created_at))
      ? Date.parse(row.created_at)
      : Date.now(),
  };
};

export const housingForecastProfilesDB = {
  getAll: async (): Promise<HousingForecastProfile[]> => {
    const { data, error } = await supabase
      .from("housing_forecast_profiles")
      .select("id,name,settings,created_at")
      .order("created_at", { ascending: true });
    if (error) {
      console.error("[housingForecastProfilesDB] getAll failed", error);
      return [];
    }
    return (data || []).map(mapDbToProfile);
  },

  upsert: async (
    profile: HousingForecastProfile
  ): Promise<HousingForecastProfile | null> => {
    const { data, error } = await supabase
      .from("housing_forecast_profiles")
      .upsert(
        [
          {
            id: profile.id,
            name: profile.name,
            settings: profile.settings,
          },
        ],
        { onConflict: "id" }
      )
      .select("id,name,settings,created_at")
      .single();
    if (error) {
      console.error("[housingForecastProfilesDB] upsert failed", error);
      return null;
    }
    notifyHousingForecastProfilesChanged();
    return mapDbToProfile(data as DbHousingForecastProfile);
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from("housing_forecast_profiles")
      .delete()
      .eq("id", id);
    if (error) {
      console.error("[housingForecastProfilesDB] delete failed", error);
      return false;
    }
    notifyHousingForecastProfilesChanged();
    return true;
  },
};
